import { Col, Row, Text } from 'components';
import React from 'react';
import {
  ANCHOR_EDGE,
  WAVE_PRESETS,
  anchorFromDraft,
  anchorToDraft,
  matchPreset,
  presetByKey
} from '../anchors';
import { toDatetimeLocal } from '../waveEditPayload';
import AnchorPicker from './AnchorPicker';
import { FieldError, FieldHint, Select } from './timelineUi';

// A Game Day wave's SCHEDULE, wherever it is edited: the preset dropdown above the two edge
// pickers, and nothing else.
//
// Shared by the timeline's "add wave" editor and the create modal deliberately. The schedule is
// one grammar now, and two implementations of it is precisely the thing the anchor rebuild exists
// to remove — the old modal could express three of the combinations and the timeline all of them,
// so which surface you opened decided what you could schedule.
//
// Controlled: the drafts and the preset selection live in the parent, because the two parents
// submit differently (POST /create from the modal, POST /create plus a timeline refetch from the
// track). This owns the layout and the preset wiring only.

const AnchorScheduleFields = ({
  openDraft,
  closeDraft,
  onOpenChange,
  onCloseChange,
  presetKey,
  onPresetKeyChange,
  postFtMinutes,
  phaseByEvent,
  openResolved,
  validation,
  orderError,
  // `datetime-local` bounds for the two absolute fields — the times already given away.
  bounds,
  openDisabled,
  openDisabledReason,
  closeKinds
}) => {
  const applyPreset = (key) => {
    onPresetKeyChange(key);
    const preset = presetByKey(key);
    if (!preset) return;
    const seeded = preset.seed({
      postFtMinutes,
      // The presets that end (or start) on the wall clock keep whatever the admin already typed,
      // so picking one never wipes a date they had entered.
      at: closeDraft.at || openDraft.at,
      openAt: openDraft.at,
      open: preset.keepsOpen ? anchorFromDraft(openDraft) : undefined
    });
    if (!preset.keepsOpen) onOpenChange(anchorToDraft(seeded.open, toDatetimeLocal));
    onCloseChange(anchorToDraft(seeded.close, toDatetimeLocal));
  };

  // Which preset the current pair IS, so a hand-built schedule reads as "Custom" rather than
  // silently claiming the last preset picked.
  const currentPreset = matchPreset(anchorFromDraft(openDraft), anchorFromDraft(closeDraft), {
    postFtMinutes
  });

  return (
    <Col noFlex wid="100%" gap="13px">
      <Col noFlex wid="100%" style={{ maxWidth: 420 }}>
        <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
          Preset
        </Text>
        <Select
          value={currentPreset?.key || presetKey || ''}
          disabled={openDisabled}
          onChange={(e) => applyPreset(e.target.value)}
          aria-label="Schedule preset"
        >
          <option value="">{currentPreset ? currentPreset.label : 'Custom'}</option>
          {WAVE_PRESETS.filter((p) => p.group === 'core').map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
          {WAVE_PRESETS.filter((p) => p.group === 'extra').map((p) => (
            <option key={p.key} value={p.key}>
              {`· ${p.label}`}
            </option>
          ))}
        </Select>
        <FieldHint>
          {presetByKey(presetKey)?.hint ||
            currentPreset?.hint ||
            'A preset only seeds the two pickers below — edit either edge afterwards, or drag its handle on the timeline.'}
        </FieldHint>
      </Col>

      <Row noFlex wid="100%" gap="12px" style={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Col noFlex style={{ flex: '1 1 260px', minWidth: 240 }}>
          <AnchorPicker
            edge={ANCHOR_EDGE.OPEN}
            draft={openDraft}
            onChange={onOpenChange}
            error={validation?.open}
            disabled={openDisabled}
            disabledReason={openDisabledReason}
            phaseByEvent={phaseByEvent}
            postFtMinutes={postFtMinutes}
            min={bounds?.openMin}
            max={bounds?.openMax}
          />
        </Col>
        <Col noFlex style={{ flex: '1 1 260px', minWidth: 240 }}>
          <AnchorPicker
            edge={ANCHOR_EDGE.CLOSE}
            draft={closeDraft}
            onChange={(next) =>
              onCloseChange(
                // A restricted close (a live wave, which can only close on a date-time or a match
                // minute) refuses a kind it has no legacy field to travel on.
                closeKinds && !closeKinds.includes(next.kind) ? closeDraft : next
              )
            }
            error={validation?.close}
            phaseByEvent={phaseByEvent}
            postFtMinutes={postFtMinutes}
            openResolved={openResolved}
            min={bounds?.closeMin}
            max={bounds?.closeMax}
          />
        </Col>
      </Row>

      {validation?.pair && <FieldError>{validation.pair}</FieldError>}
      {orderError && <FieldError>{orderError}</FieldError>}
    </Col>
  );
};

export default AnchorScheduleFields;
